import { toastState } from "@/atoms/system/toast-state";
import { ButtonColor } from "@/common/enum/button-color";
import { TextInputColor } from "@/common/enum/text-input-color";
import { validatePassword } from "@/common/validator/validate-password";
import { CustomHelperText } from "@/components/CustomHelperText";
import { PasswordEyeIcon } from "@/components/icon/PasswordEyeIcon";
import { TextInputLabel } from "@/components/label/TextInputLabel";
import { H4 } from "@/components/typography/H4";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { userService } from "@/services/user-service";
import { UpdatePartnerPasswordPayload } from "@/types/payload/update-partner-password-payload";
import { Button, TextInput } from "flowbite-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useSetRecoilState } from "recoil";

export default function PasswordUpdatePage() {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const setToast = useSetRecoilState(toastState);
  const [aggregateErrors, setAggregateErrors] = useState({
    password: "",
    confirmedPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmedPassword = () => {
    setShowConfirmedPassword(!showConfirmedPassword);
  };

  const handleConfirmedPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setConfirmedPassword(value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setPassword(value);
  };

  const handleClickButton = async () => {
    try {
      if (!validatePassword(password)) {
        setAggregateErrors({
          ...aggregateErrors,
          password: "특수문자, 영어, 숫자 조합으로 8~15자를 입력해주세요.",
        });
        return;
      }

      if (!validatePassword(confirmedPassword)) {
        setAggregateErrors({
          ...aggregateErrors,
          confirmedPassword:
            "특수문자, 영어, 숫자 조합으로 8~15자를 입력해주세요.",
        });
        return;
      }

      if (password !== confirmedPassword) {
        setAggregateErrors({
          ...aggregateErrors,
          confirmedPassword: "비밀번호가 일치하지 않습니다.",
        });
        return;
      }

      if (!token) {
        return;
      }

      const payload: UpdatePartnerPasswordPayload = {
        token,
        newPassword: password,
      };
      const response = await userService.updatePartnerPassword(payload);
      setAggregateErrors({
        password: response.data.aggregateErrors?.newPassword || "",
        confirmedPassword: response.data.aggregateErrors?.newPassword || "",
      });
      if (response.ok) {
        setToast({
          show: true,
          messages: [`비밀번호가 변경되었습니다.`],
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <PageWrapper>
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col justify-center w-[400px]">
          <div className="flex items-center justify-center">
            <H4 className="text-semantic-text-heading mb-[32px]">
              신규 비밀번호 설정
            </H4>
          </div>
          <TextInputLabel required>비밀번호</TextInputLabel>
          {/* TODO helperText 특수문자, 영어, 숫자 조합으로 8~15자를 입력해주세요. */}
          <TextInput
            required
            placeholder="비밀번호를 입력해주세요"
            type={showPassword ? "text" : "password"}
            rightIcon={() => {
              return (
                <PasswordEyeIcon
                  showPassword={showPassword}
                  isError={!!aggregateErrors.password}
                  onClick={toggleShowPassword}
                />
              );
            }}
            color={
              !!aggregateErrors.password
                ? TextInputColor.FAILURE
                : TextInputColor.GRAY
            }
            value={password}
            onChange={handlePasswordChange}
          />
          <CustomHelperText
            isError={!!aggregateErrors.password}
            text={
              aggregateErrors.password ||
              "특수문자, 영어, 숫자 조합으로 8~15자를 입력해주세요."
            }
          />
          <div className="mt-[16px]">
            <TextInputLabel required>비밀번호 확인</TextInputLabel>
          </div>
          <TextInput
            required
            placeholder="비밀번호를 한 번 더 입력해주세요"
            type={showConfirmedPassword ? "text" : "password"}
            rightIcon={() => {
              return (
                <PasswordEyeIcon
                  showPassword={showConfirmedPassword}
                  isError={!!aggregateErrors.confirmedPassword}
                  onClick={toggleShowConfirmedPassword}
                />
              );
            }}
            color={
              !!aggregateErrors.confirmedPassword
                ? TextInputColor.FAILURE
                : TextInputColor.GRAY
            }
            value={confirmedPassword}
            onChange={handleConfirmedPasswordChange}
          />
          {!!aggregateErrors.confirmedPassword && (
            <CustomHelperText
              isError={!!aggregateErrors.confirmedPassword}
              text={aggregateErrors.confirmedPassword}
            />
          )}
          <Button
            className="mt-[32px]"
            color={ButtonColor.PRIMARY}
            onClick={handleClickButton}
          >
            비밀번호 변경
          </Button>
          <div className="flex justify-center">
            <img
              src="/img/logo_black.png"
              alt="Logo"
              className="w-[103px] h-[24px] mt-[32px]"
            />
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}
